import type { TypeUser } from '../../../types/User'
import type { InventoryTransfer, InventoryTransferListItem } from '../types'
import { TRANSFER_STATUS } from '../../../constants/TransferStatus'
import { getActiveInventoryLocationOption } from './locationLocking'

export type TransferListTabMode = 'incoming' | 'outgoing' | 'history'

export type TransferContextFilter = {
  id: string
  column: string
  search: string
  operator: 'AND'
}

/**
 * Active inventory location id from the authenticated user session.
 * @param user - Auth user payload.
 * @returns Location id, or null when no active inventory location is selected.
 */
export function getUserActiveInventoryLocationId(user?: TypeUser | null): number | null {
  return getActiveInventoryLocationOption(user?.active_inventory_location)?.id ?? null
}

/**
 * Active inventory location display name from the authenticated user session.
 * @param user - Auth user payload.
 * @returns Location name, or null when none is selected.
 */
export function getUserActiveInventoryLocationName(user?: TypeUser | null): string | null {
  return getActiveInventoryLocationOption(user?.active_inventory_location)?.name ?? null
}

/**
 * Destination location id on a transfer resource.
 * @param transfer - Transfer list or detail resource.
 * @returns Numeric destination location id, or null.
 */
export function transferDestinationLocationId(transfer: Pick<InventoryTransfer, 'destination'>): number | null {
  const id = transfer.destination?.location_id
  if (id == null || !Number.isFinite(Number(id))) return null
  return Number(id)
}

/**
 * Whether the user's active inventory location is the transfer destination.
 * @param transfer - Transfer list or detail resource.
 * @param activeLocationId - Active inventory location id from session.
 * @returns True when the user is operating at the receiving location.
 */
export function isAtReceivingLocation(
  transfer: Pick<InventoryTransfer, 'destination'>,
  activeLocationId: number | null,
): boolean {
  if (activeLocationId == null) return false
  const destinationId = transferDestinationLocationId(transfer)
  return destinationId != null && destinationId === activeLocationId
}

/**
 * Whether the user may receive a sent transfer at their current location.
 * @param transfer - Transfer list or detail resource.
 * @param activeLocationId - Active inventory location id from session.
 * @param hasCreatePermission - User has stock-transfer create permission.
 * @returns True when receive UI/actions should be shown.
 */
export function canReceiveTransfer(
  transfer: Pick<InventoryTransfer, 'status' | 'destination'> | null | undefined,
  activeLocationId: number | null,
  hasCreatePermission: boolean,
): boolean {
  if (!transfer || !hasCreatePermission) return false
  if (transfer.status !== TRANSFER_STATUS.SENT) return false
  return isAtReceivingLocation(transfer, activeLocationId)
}

/**
 * Whether a list row should expose the Receive action (incoming tab only).
 * @param transfer - Transfer list row.
 * @param tabMode - Active transfers tab.
 * @param activeLocationId - Active inventory location id from session.
 * @param hasCreatePermission - User has stock-transfer create permission.
 * @returns True when the row should show Receive instead of View.
 */
export function canShowReceiveActionOnListRow(
  transfer: InventoryTransferListItem,
  tabMode: TransferListTabMode,
  activeLocationId: number | null,
  hasCreatePermission: boolean,
): boolean {
  return tabMode === 'incoming' && canReceiveTransfer(transfer, activeLocationId, hasCreatePermission)
}

/**
 * Builds locked list filters that scope a tab to the user's active inventory location.
 * @param tabMode - Incoming, outgoing, or history tab.
 * @param activeLocationId - Active inventory location id from session.
 * @returns Filter objects merged into every list request for the tab.
 */
export function buildTransferTabLocationFilters(
  tabMode: TransferListTabMode,
  activeLocationId: number | null,
): TransferContextFilter[] {
  if (activeLocationId == null) return []

  switch (tabMode) {
    case 'incoming':
      return [
        {
          id: 'transfer_ctx_destination',
          column: 'destination_location_id',
          search: String(activeLocationId),
          operator: 'AND',
        },
      ]
    case 'outgoing':
      return [
        {
          id: 'transfer_ctx_source',
          column: 'source_location_id',
          search: String(activeLocationId),
          operator: 'AND',
        },
      ]
    case 'history':
      return [
        {
          id: 'transfer_ctx_location',
          column: 'location_id',
          search: String(activeLocationId),
          operator: 'AND',
        },
      ]
  }
}

/**
 * Status filter locked per tab (Incoming/Outgoing = SENT only; History = all statuses).
 * @param tabMode - Incoming, outgoing, or history tab.
 * @returns Status code to always apply, or null when any status is allowed.
 */
export function getTransferTabLockedStatus(tabMode: TransferListTabMode): number | null {
  switch (tabMode) {
    case 'incoming':
    case 'outgoing':
      return TRANSFER_STATUS.SENT
    case 'history':
      return null
  }
}

/**
 * API list direction for a transfers tab (null = either participating location).
 * @param tabMode - Incoming, outgoing, or history tab.
 * @returns Direction query param value.
 */
export function getTransferTabDirection(tabMode: TransferListTabMode): 'incoming' | 'outgoing' | null {
  switch (tabMode) {
    case 'incoming':
      return 'incoming'
    case 'outgoing':
      return 'outgoing'
    default:
      return null
  }
}
